/**
 * Account-hub: profiel, organisatie (admin), beveiliging, dossiers, voorkeuren — overzichtelijke flow.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Building2,
  CreditCard,
  Euro,
  FolderOpen,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  MonitorSmartphone,
  Sparkles,
  Trash2,
  Upload,
  UserCircle,
  UserPlus,
} from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { ThemeToggle } from '../components/ThemeToggle';
import { UserAvatar } from '../components/UserAvatar';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function roleLabel(role: string | null | undefined): string {
  if (role === 'superadmin') return 'Platformbeheerder';
  if (role === 'admin') return 'Accountbeheerder';
  if (role === 'user') return 'Gebruiker';
  return role || '—';
}

export function Account() {
  const { isAccountManager, isSuperAdmin, user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [avatarBump, setAvatarBump] = useState(0);

  const [profileMsg, setProfileMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [tenantMsg, setTenantMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [tenantBusy, setTenantBusy] = useState(false);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const inviteQ = useQuery({
    queryKey: ['me-invitations'],
    queryFn: () => api.getMeInvitations(),
    enabled: isAccountManager && !!user?.authenticated,
  });

  const inviteMut = useMutation({
    mutationFn: (em: string) => api.createMeInvitation({ email: em }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-invitations'] });
      setInviteEmail('');
      setInviteMsg({ type: 'ok', text: 'Uitnodiging verstuurd. Je collega ontvangt een e-mail met een persoonlijke link.' });
    },
    onError: (err: unknown) => {
      setInviteMsg({ type: 'err', text: err instanceof Error ? err.message : 'Uitnodigen mislukt' });
    },
  });

  const revokeInviteMut = useMutation({
    mutationFn: (id: string) => api.revokeMeInvitation(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me-invitations'] }),
  });

  useEffect(() => {
    setDisplayName((user?.display_name ?? '').trim());
    setTenantName((user?.tenant_display_name ?? '').trim());
  }, [user?.display_name, user?.tenant_display_name]);

  const tenantId = user?.tenant_id ?? 'default';
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', tenantId],
    queryFn: () => api.listProjects(tenantId, { limit: 30 }),
    enabled: !!user?.authenticated,
  });

  const delMut = useMutation({
    mutationFn: (projectId: string) => api.deleteProject(projectId, tenantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const onSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileBusy(true);
    try {
      await api.patchMeProfile({ display_name: displayName.trim() });
      await refreshUser();
      setProfileMsg({ type: 'ok', text: 'Profiel opgeslagen.' });
    } catch (err) {
      setProfileMsg({ type: 'err', text: err instanceof Error ? err.message : 'Opslaan mislukt' });
    } finally {
      setProfileBusy(false);
    }
  };

  const onSaveTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setTenantMsg(null);
    const nm = tenantName.trim();
    if (!nm) {
      setTenantMsg({ type: 'err', text: 'Vul een organisatienaam in.' });
      return;
    }
    setTenantBusy(true);
    try {
      await api.patchMeTenant({ tenant_display_name: nm });
      await refreshUser();
      setTenantMsg({ type: 'ok', text: 'Organisatie bijgewerkt.' });
    } catch (err) {
      setTenantMsg({ type: 'err', text: err instanceof Error ? err.message : 'Opslaan mislukt' });
    } finally {
      setTenantBusy(false);
    }
  };

  const onAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setAvatarBusy(true);
    try {
      await api.uploadMeAvatar(f);
      window.dispatchEvent(new Event('calq-avatar-updated'));
      setAvatarBump((n) => n + 1);
      await refreshUser();
    } catch (err) {
      setProfileMsg({ type: 'err', text: err instanceof Error ? err.message : 'Upload mislukt' });
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async () => {
    if (!globalThis.confirm('Profielfoto verwijderen?')) return;
    setAvatarBusy(true);
    try {
      await api.deleteMeAvatar();
      window.dispatchEvent(new Event('calq-avatar-updated'));
      setAvatarBump((n) => n + 1);
      await refreshUser();
    } catch (err) {
      setProfileMsg({ type: 'err', text: err instanceof Error ? err.message : 'Verwijderen mislukt' });
    } finally {
      setAvatarBusy(false);
    }
  };

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (next !== next2) {
      setPwMsg({ type: 'err', text: 'Nieuwe wachtwoorden komen niet overeen.' });
      return;
    }
    if (next.length < 8) {
      setPwMsg({ type: 'err', text: 'Nieuw wachtwoord minimaal 8 tekens.' });
      return;
    }
    setPwBusy(true);
    try {
      await api.authChangePassword({ current_password: current, new_password: next });
      setPwMsg({ type: 'ok', text: 'Wachtwoord gewijzigd.' });
      setCurrent('');
      setNext('');
      setNext2('');
    } catch (err) {
      setPwMsg({ type: 'err', text: err instanceof Error ? err.message : 'Mislukt' });
    } finally {
      setPwBusy(false);
    }
  };

  const navItems: { id: string; label: string }[] = [
    { id: 'account-overzicht', label: 'Overzicht' },
    { id: 'account-profiel', label: 'Profiel' },
    ...(isAccountManager
      ? [
          { id: 'account-organisatie', label: 'Organisatie' },
          { id: 'account-team', label: 'Team & uitnodigingen' },
        ]
      : []),
    { id: 'account-beveiliging', label: 'Beveiliging' },
    { id: 'account-sessies', label: 'Inloggen & apparaten' },
    { id: 'account-dossiers', label: 'Dossiers' },
    { id: 'account-voorkeuren', label: 'Voorkeuren' },
    { id: 'account-snel', label: 'Snelkoppelingen' },
    { id: 'account-hulp', label: 'Hulp' },
  ];

  const scrollToId = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <SeoMeta
        path="/account"
        noindex
        title="Mijn account"
        description="Mijn CalqTool™-account: profiel, organisatie, voorkeuren en overzicht van je omgeving — alleen zichtbaar na inloggen."
      />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Account' }]} />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Mijn account</h1>
          <p className="mt-1 text-sm text-text-muted">Beheer je profiel, voorkeuren en dossiers op één plek.</p>
        </div>
      </div>

      <nav
        className="sticky top-0 z-10 -mx-1 mb-8 flex flex-wrap gap-1.5 border-b border-border bg-surface/95 px-1 py-2 backdrop-blur-sm sm:gap-2"
        aria-label="Secties account"
      >
        {navItems.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollToId(id)}
            className="rounded-full border border-border/80 bg-surface-card px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-primary/40 hover:text-primary sm:text-sm"
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="space-y-10">
        <section id="account-overzicht" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-4 text-lg font-semibold text-text">Overzicht</h2>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <UserAvatar
              displayName={user?.display_name}
              email={user?.email}
              hasAvatar={user?.has_avatar}
              size={72}
              imageVersion={avatarBump}
              className="shrink-0"
            />
            <div className="min-w-0 flex-1 space-y-2 text-sm">
              <p className="text-text">
                <span className="text-text-muted">E-mail</span>
                <br />
                <strong className="font-medium">{user?.email ?? '—'}</strong>
              </p>
              <p className="text-text">
                <span className="text-text-muted">Weergavenaam</span>
                <br />
                <strong className="font-medium">
                  {user?.display_name?.trim() || user?.email?.split('@')[0] || '—'}
                </strong>
              </p>
              <p className="text-text">
                <span className="text-text-muted">Rol</span>
                <br />
                <strong className="font-medium">{roleLabel(user?.role)}</strong>
              </p>
              {user?.tenant_display_name && (
                <p className="text-text">
                  <span className="text-text-muted">Organisatie</span>
                  <br />
                  <strong className="font-medium">{user.tenant_display_name}</strong>
                </p>
              )}
            </div>
          </div>
        </section>

        <section id="account-profiel" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
            <UserCircle className="h-5 w-5 text-primary" aria-hidden />
            Profiel & foto
          </h2>
          <p className="mb-5 text-sm text-text-muted">
            Je weergavenaam zien collega’s in de app; je e-mailadres is je inlognaam en kun je hier niet wijzigen.
          </p>

          <div className="mb-6 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
            <UserAvatar
              displayName={user?.display_name}
              email={user?.email}
              hasAvatar={user?.has_avatar}
              size={64}
              imageVersion={avatarBump}
            />
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => void onAvatarPick(e)} />
              <button
                type="button"
                disabled={avatarBusy}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium hover:border-primary hover:text-primary disabled:opacity-50"
              >
                <Upload className="h-4 w-4" aria-hidden />
                {avatarBusy ? 'Bezig…' : 'Foto uploaden'}
              </button>
              {user?.has_avatar && (
                <button
                  type="button"
                  disabled={avatarBusy}
                  onClick={() => void onRemoveAvatar()}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Foto verwijderen
                </button>
              )}
            </div>
            <p className="w-full text-xs text-text-muted sm:w-auto sm:flex-1">PNG, JPEG, WebP of GIF, max. 2 MB.</p>
          </div>

          <form onSubmit={(e) => void onSaveProfile(e)} className="max-w-lg space-y-4">
            <div>
              <label htmlFor="acc-display" className="mb-1 block text-sm font-medium text-text">
                Weergavenaam
              </label>
              <input
                id="acc-display"
                type="text"
                maxLength={80}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={user?.email?.split('@')[0] ?? 'Je naam'}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
              />
              <p className="mt-1 text-xs text-text-muted">Max. 80 tekens. Leeg laten = alleen initialen uit je e-mail.</p>
            </div>
            {profileMsg && (
              <p
                className={
                  profileMsg.type === 'ok'
                    ? 'rounded-lg bg-primary/10 px-3 py-2 text-sm text-text'
                    : 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100'
                }
                role="status"
              >
                {profileMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={profileBusy}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
            >
              {profileBusy ? 'Opslaan…' : 'Profiel opslaan'}
            </button>
          </form>
        </section>

        {isAccountManager && (
          <section id="account-organisatie" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
              <Building2 className="h-5 w-5 text-primary" aria-hidden />
              Organisatie
            </h2>
            <p className="mb-5 text-sm text-text-muted">
              De naam van je organisatie wordt getoond waar relevant voor je team (bijv. facturatie en overzichten).
            </p>
            {user?.tenant_billing &&
            typeof user.tenant_billing.user_count === 'number' &&
            typeof user.tenant_billing.limits?.max_seats === 'number' ? (
              <p className="mb-4 rounded-lg border border-border/80 bg-surface px-3 py-2 text-sm text-text-muted">
                <strong className="text-text">Aantal gebruikers in jullie pakket:</strong>{' '}
                {user.tenant_billing.user_count} van {user.tenant_billing.limits.max_seats}. Extra collega’s nodig? Kies
                een hoger pakket onder{' '}
                <Link to="/account/billing" className="font-medium text-primary hover:underline">
                  Account → Facturatie
                </Link>
                . Tijdens de proefperiode kun je nog geen teamleden uitnodigen — dat kan zodra je een abonnement hebt
                gekozen.
              </p>
            ) : null}
            <form onSubmit={(e) => void onSaveTenant(e)} className="max-w-lg space-y-4">
              <div>
                <label htmlFor="acc-tenant" className="mb-1 block text-sm font-medium text-text">
                  Organisatienaam
                </label>
                <input
                  id="acc-tenant"
                  type="text"
                  maxLength={120}
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  required
                />
              </div>
              {tenantMsg && (
                <p
                  className={
                    tenantMsg.type === 'ok'
                      ? 'rounded-lg bg-primary/10 px-3 py-2 text-sm text-text'
                      : 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100'
                  }
                  role="status"
                >
                  {tenantMsg.text}
                </p>
              )}
              <button
                type="submit"
                disabled={tenantBusy}
                className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
              >
                {tenantBusy ? 'Opslaan…' : 'Organisatie opslaan'}
              </button>
            </form>
          </section>
        )}

        {isAccountManager && (
          <section id="account-team" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
              <UserPlus className="h-5 w-5 text-primary" aria-hidden />
              Team & uitnodigingen
            </h2>
            <p className="mb-5 text-sm text-text-muted">
              Nodig collega’s uit om in <strong className="text-text">hetzelfde bedrijfsaccount</strong> te werken als
              jij. Zij krijgen een e-mail met een persoonlijke link en kiezen daar hun wachtwoord.{' '}
              <strong className="text-text">Let op:</strong> als iemand zonder uitnodiging via “account aanmaken” op de
              website start, ontstaat een <em>apart</em> bedrijfsaccount — dat willen we voorkomen.
            </p>

            {inviteQ.isLoading && <p className="text-sm text-text-muted">Teamgegevens laden…</p>}
            {inviteQ.data && (
              <>
                <div
                  className={`mb-5 rounded-lg border px-3 py-2.5 text-sm ${
                    inviteQ.data.limits.invites_allowed
                      ? 'border-primary/25 bg-primary/5 text-text'
                      : 'border-amber-200/90 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50'
                  }`}
                >
                  {inviteQ.data.limits.invites_allowed ? (
                    <p>
                      Je kunt nu uitnodigingen sturen. Gebruik:{' '}
                      <strong className="text-text">
                        {inviteQ.data.limits.current_users} van {inviteQ.data.limits.max_users}
                      </strong>{' '}
                      plekken bezet
                      {inviteQ.data.limits.pending_invites > 0
                        ? ` (${inviteQ.data.limits.pending_invites} openstaande uitnodiging${inviteQ.data.limits.pending_invites === 1 ? '' : 'en'})`
                        : ''}
                      .
                    </p>
                  ) : (
                    <p>{inviteQ.data.limits.invite_blocked_reason ?? 'Uitnodigen is nu niet mogelijk.'}</p>
                  )}
                </div>

                <div className="mb-6">
                  <h3 className="mb-2 text-sm font-semibold text-text">Wie heeft toegang</h3>
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {(inviteQ.data.members ?? []).map((m) => (
                      <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm">
                        <span className="font-mono text-text">{m.email}</span>
                        <span className="text-text-muted">{roleLabel(m.role)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {inviteQ.data.limits.invites_allowed && (
                  <form
                    className="mb-6 max-w-lg space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setInviteMsg(null);
                      const em = inviteEmail.trim().toLowerCase();
                      if (!em || !em.includes('@')) {
                        setInviteMsg({ type: 'err', text: 'Vul een geldig e-mailadres in.' });
                        return;
                      }
                      inviteMut.mutate(em);
                    }}
                  >
                    <h3 className="text-sm font-semibold text-text">Collega uitnodigen</h3>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="min-w-0 flex-1">
                        <label htmlFor="invite-email" className="mb-1 block text-xs font-medium text-text-muted">
                          E-mailadres collega
                        </label>
                        <input
                          id="invite-email"
                          type="email"
                          autoComplete="off"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="naam@bedrijf.nl"
                          className="w-full rounded-lg border border-border px-3 py-2 text-text"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={inviteMut.isPending}
                        className="shrink-0 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
                      >
                        {inviteMut.isPending ? 'Versturen…' : 'Uitnodiging versturen'}
                      </button>
                    </div>
                    {inviteMsg && (
                      <p
                        className={
                          inviteMsg.type === 'ok'
                            ? 'rounded-lg bg-primary/10 px-3 py-2 text-sm text-text'
                            : 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100'
                        }
                        role="status"
                      >
                        {inviteMsg.text}
                      </p>
                    )}
                  </form>
                )}

                {(inviteQ.data.pending_invitations?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-text">Openstaande uitnodigingen</h3>
                    <p className="mb-2 text-xs text-text-muted">Geldig 7 dagen. Trek een uitnodiging in als je per ongeluk het verkeerde adres hebt gebruikt.</p>
                    <ul className="divide-y divide-border rounded-lg border border-border">
                      {inviteQ.data.pending_invitations.map((p) => {
                        const exp = new Date(p.expires_at);
                        const expStr = Number.isFinite(exp.getTime())
                          ? exp.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })
                          : p.expires_at;
                        return (
                          <li
                            key={p.invitation_id}
                            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"
                          >
                            <div>
                              <span className="font-mono text-text">{p.email}</span>
                              <span className="ml-2 text-xs text-text-muted">tot {expStr}</span>
                            </div>
                            <button
                              type="button"
                              disabled={revokeInviteMut.isPending}
                              onClick={() => revokeInviteMut.mutate(p.invitation_id)}
                              className="text-xs font-medium text-red-700 hover:underline dark:text-red-300"
                            >
                              Intrekken
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        <section id="account-beveiliging" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
            <KeyRound className="h-5 w-5 text-primary" aria-hidden />
            Beveiliging
          </h2>
          <p className="mb-5 text-sm text-text-muted">
            Wijzig je wachtwoord regelmatig en gebruik een uniek wachtwoord. Je server-sessie kan tot dertig dagen geldig
            zijn; in de app word je na <strong className="text-text">ongeveer 45 minuten zonder activiteit</strong>{' '}
            automatisch uitgelogd (bijv. gedeelde computer).
          </p>
          <form onSubmit={(e) => void onChangePassword(e)} className="grid max-w-md gap-3">
            <div>
              <label htmlFor="acc-cur" className="mb-1 block text-sm font-medium text-text">
                Huidig wachtwoord
              </label>
              <input
                id="acc-cur"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
                required
              />
            </div>
            <div>
              <label htmlFor="acc-new" className="mb-1 block text-sm font-medium text-text">
                Nieuw wachtwoord
              </label>
              <input
                id="acc-new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
                required
                minLength={8}
              />
            </div>
            <div>
              <label htmlFor="acc-new2" className="mb-1 block text-sm font-medium text-text">
                Bevestig nieuw wachtwoord
              </label>
              <input
                id="acc-new2"
                type="password"
                autoComplete="new-password"
                value={next2}
                onChange={(e) => setNext2(e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
                required
                minLength={8}
              />
            </div>
            {pwMsg && (
              <p
                className={
                  pwMsg.type === 'ok'
                    ? 'rounded-lg bg-primary/10 px-3 py-2 text-sm text-text'
                    : 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100'
                }
                role="status"
              >
                {pwMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={pwBusy}
              className="w-full max-w-xs rounded-lg bg-primary py-2.5 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
            >
              {pwBusy ? 'Bezig…' : 'Wachtwoord opslaan'}
            </button>
          </form>
        </section>

        <section id="account-sessies" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
            <MonitorSmartphone className="h-5 w-5 text-primary" aria-hidden />
            Inloggen &amp; apparaten
          </h2>
          <p className="mb-4 text-sm text-text-muted">
            Je pakket bepaalt hoeveel browsers of apparaten tegelijk ingelogd mogen zijn met{' '}
            <strong className="text-text">ditzelfde e-mailaccount</strong>. Basis: één tegelijk; Plus: tot drie; Pro: tot
            vijf (Enterprise ruimer; proefperiode volgt de Plus-richtlijn). Als je opnieuw inlogt terwijl je het maximum
            bereikt hebt, wordt de <strong className="text-text">oudste</strong> actieve sessie beëindigd — het andere
            apparaat moet dan opnieuw inloggen.
          </p>
          {user?.tenant_billing &&
          typeof user.tenant_billing.limits.max_concurrent_devices === 'number' ? (
            <ul className="mb-4 space-y-2 rounded-lg border border-border/80 bg-surface/50 p-4 text-sm text-text">
              <li>
                <span className="text-text-muted">Maximum gelijktijdige apparaten voor jouw account:</span>{' '}
                <strong className="text-text">{user.tenant_billing.limits.max_concurrent_devices}</strong>
              </li>
              {typeof user.tenant_billing.active_sessions === 'number' ? (
                <li>
                  <span className="text-text-muted">Actieve sessies nu (inclusief dit apparaat):</span>{' '}
                  <strong className="text-text">{user.tenant_billing.active_sessions}</strong>
                </li>
              ) : null}
            </ul>
          ) : (
            <p className="mb-4 text-sm text-text-muted">Limieten worden geladen zodra je facturatiegegevens beschikbaar zijn.</p>
          )}
          {isAccountManager ? (
            <p className="text-sm text-text-muted">
              Als <strong className="text-text">accountbeheerder</strong> regel je het abonnement onder{' '}
              <Link to="/account/billing" className="font-medium text-primary hover:underline">
                Facturatie
              </Link>
              . Uurtarieven voor calculaties en offertes stel je in onder{' '}
              <Link to="/account/labor-rates" className="font-medium text-primary hover:underline">
                Uurtarieven
              </Link>
              . Meer gebruikersaccounts op jullie organisatie (collega&apos;s) zijn afhankelijk van je pakket (seats) —
              los van dit apparaatlimiet per persoon.
            </p>
          ) : (
            <p className="text-sm text-text-muted">
              Vragen over betalen of upgraden? Vraag je{' '}
              <strong className="text-text">accountbeheerder</strong> — die ziet Facturatie in het menu.
            </p>
          )}
        </section>

        <section id="account-dossiers" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-text">
            <FolderOpen className="h-5 w-5 text-primary" aria-hidden />
            Dossiers
          </h2>
          <p className="mb-4 text-sm text-text-muted">
            Bekijk en verwijder dossiers. Verwijderen is permanent en kan niet ongedaan worden gemaakt.
          </p>
          {projectsLoading && <p className="text-sm text-text-muted">Dossiers laden…</p>}
          {!projectsLoading && (projectsData?.projects?.length ?? 0) === 0 && (
            <p className="text-sm text-text-muted">Nog geen dossiers. Start via het dashboard of nieuw dossier.</p>
          )}
          {!projectsLoading && (projectsData?.projects?.length ?? 0) > 0 && (
            <ul className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-border/80 bg-surface/50 p-3">
              {((projectsData?.projects ?? []) as Array<{
                project_id: string;
                metadata?: { name?: string };
                updated_at?: string;
              }>).map((p) => (
                <li
                  key={p.project_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-transparent px-2 py-1.5 hover:border-border hover:bg-surface-card"
                >
                  <Link
                    to={`/projects/${p.project_id}/gegevens`}
                    className="min-w-0 flex-1 text-sm font-medium text-primary hover:underline"
                  >
                    {p.metadata?.name || p.project_id}
                    {p.updated_at && (
                      <span className="ml-2 font-normal text-text-muted">
                        {new Date(p.updated_at).toLocaleDateString('nl-NL')}
                      </span>
                    )}
                  </Link>
                  <button
                    type="button"
                    disabled={delMut.isPending}
                    onClick={() => {
                      const name = p.metadata?.name || p.project_id;
                      if (
                        !globalThis.confirm(
                          `Dossier “${name}” permanent verwijderen? Dit kan niet ongedaan worden gemaakt.`
                        )
                      )
                        return;
                      delMut.mutate(p.project_id);
                    }}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Verwijderen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section id="account-voorkeuren" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-1 text-lg font-semibold text-text">Voorkeuren</h2>
          <p className="mb-4 text-sm text-text-muted">Thema: licht, donker of meeschakelen met je systeem.</p>
          <ThemeToggle />
        </section>

        <section id="account-snel" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-text">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden />
            Snelkoppelingen
          </h2>
          <ul className="grid gap-2 text-sm sm:grid-cols-2">
            <li>
              <Link
                to="/dashboard"
                className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-2.5 text-text hover:border-border hover:bg-surface"
              >
                <LayoutDashboard className="h-4 w-4 shrink-0 text-text-muted" />
                Dashboard
              </Link>
            </li>
            <li>
              <Link
                to="/projects/new"
                className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-2.5 text-text hover:border-border hover:bg-surface"
              >
                <FolderOpen className="h-4 w-4 shrink-0 text-text-muted" />
                Nieuw dossier
              </Link>
            </li>
            {isAccountManager && (
              <li>
                <Link
                  to="/account/billing"
                  className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-2.5 text-text hover:border-border hover:bg-surface"
                >
                  <CreditCard className="h-4 w-4 shrink-0 text-text-muted" />
                  Abonnement & facturatie
                </Link>
              </li>
            )}
            {isAccountManager && (
              <li>
                <Link
                  to="/account/labor-rates"
                  className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-2.5 text-text hover:border-border hover:bg-surface"
                >
                  <Euro className="h-4 w-4 shrink-0 text-text-muted" />
                  Uurtarieven (calculatie & offerte)
                </Link>
              </li>
            )}
            {isSuperAdmin && (
              <li>
                <Link
                  to="/admin"
                  className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-2.5 text-text hover:border-border hover:bg-surface"
                >
                  <Building2 className="h-4 w-4 shrink-0 text-text-muted" />
                  Platformbeheer
                </Link>
              </li>
            )}
          </ul>
        </section>

        <section id="account-hulp" className="scroll-mt-24 rounded-xl border border-border bg-surface-card p-5 shadow-sm sm:p-6">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-text">
            <HelpCircle className="h-5 w-5 text-primary" aria-hidden />
            Hulp &amp; contact
          </h2>
          <p className="mb-5 text-sm text-text-muted">
            Zelfde structuur als in de footer: eerst <strong className="text-text">service</strong> (status, veelvoorkomende
            problemen, contact), daarna <strong className="text-text">handleidingen</strong> met diepgaande uitleg per
            onderwerp.
          </p>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Service</h3>
          <ul className="mb-5 space-y-2 text-sm">
            <li>
              <Link
                to="/status"
                className="flex items-center gap-2 rounded-lg border border-transparent px-1 py-1 text-primary hover:border-border hover:bg-surface"
              >
                <Activity className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                Dienststatus — is CalqTool online?
              </Link>
            </li>
            <li>
              <Link
                to="/troubleshoot"
                className="flex items-center gap-2 rounded-lg border border-transparent px-1 py-1 text-primary hover:border-border hover:bg-surface"
              >
                <LifeBuoy className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                Problemen oplossen — veelvoorkomende oorzaken
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className="flex items-center gap-2 rounded-lg border border-transparent px-1 py-1 text-primary hover:border-border hover:bg-surface"
              >
                <Mail className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                Contact &amp; support
              </Link>
            </li>
          </ul>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Handleidingen</h3>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/help/tekening" className="text-primary hover:underline">
                Tekening &amp; PDF — documentenbibliotheek en analyse
              </Link>
            </li>
            <li>
              <Link to="/help/export" className="text-primary hover:underline">
                Export &amp; offerte — rapportage-tab
              </Link>
            </li>
            <li>
              <Link to="/help/bibliotheek" className="text-primary hover:underline">
                Prijsbibliotheek — posten, catalogus, fair use
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
